/**
 * KBUploadDialog — upload form for legislation documents.
 * US-007 Step 2: title, source, doc_type, jurisdiction, effective_date, file.
 * WCAG 2.1 AA: labels with htmlFor, aria-describedby for errors, focus trap via Radix Dialog.
 */
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Info } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useKBUpload, type DocType } from '@/hooks/useKBDocuments';
import { KBFileDropzone } from './KBFileDropzone';

const schema = z.object({
  title: z.string().min(1, 'Title is required').max(500, 'Max 500 characters'),
  source: z.string().min(1, 'Source is required').max(200, 'Max 200 characters'),
  doc_type: z.enum(['legislation', 'regulation', 'directive', 'guideline', 'internal_policy'], {
    required_error: 'Document type is required',
  }),
  jurisdiction: z.string().max(50).optional(),
  effective_date: z.string().optional(),
});

type UploadFormValues = z.infer<typeof schema>;

const DOC_TYPE_OPTIONS: { value: DocType; label: string }[] = [
  { value: 'legislation', label: 'Legislation' },
  { value: 'regulation', label: 'Regulation' },
  { value: 'directive', label: 'Directive' },
  { value: 'guideline', label: 'Guideline' },
  { value: 'internal_policy', label: 'Internal Policy' },
];

interface KBUploadDialogProps {
  open: boolean;
  onClose: () => void;
}

export function KBUploadDialog({ open, onClose }: KBUploadDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const { mutate: upload, isPending } = useKBUpload();

  const { register, handleSubmit, reset, formState: { errors } } = useForm<UploadFormValues>({
    resolver: zodResolver(schema),
  });

  const handleClose = () => {
    if (isPending) return;
    reset();
    setFile(null);
    setFileError(null);
    onClose();
  };

  const onSubmit = (values: UploadFormValues) => {
    if (!file) { setFileError('Please select a file.'); return; }
    setFileError(null);
    upload({
      file,
      title: values.title,
      source: values.source,
      doc_type: values.doc_type,
      jurisdiction: values.jurisdiction || undefined,
      effective_date: values.effective_date || undefined,
    }, { onSuccess: handleClose });
  };

  const fieldClass = (hasError: boolean) => cn(
    'w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    hasError && 'border-destructive',
  );

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <AlertDialogContent className="max-w-xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Upload Legislation Document</AlertDialogTitle>
          <AlertDialogDescription>
            Upload a PDF, TXT, HTML, or DOCX file. After upload the document will be chunked and indexed for AI retrieval.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <form id="kb-upload-form" onSubmit={(e) => { void handleSubmit(onSubmit)(e); }} className="space-y-4 mt-2" noValidate>
          <KBFileDropzone file={file} onFileChange={setFile} error={fileError} disabled={isPending} />

          <div className="space-y-1">
            <label htmlFor="kb-title" className="text-sm font-medium text-foreground">
              Title <span aria-hidden="true" className="text-destructive">*</span>
            </label>
            <input id="kb-title" type="text" placeholder="e.g., GDPR Full Text"
              aria-required="true" aria-invalid={!!errors.title}
              aria-describedby={errors.title ? 'kb-title-error' : undefined}
              className={fieldClass(!!errors.title)} {...register('title')} />
            {errors.title && <p id="kb-title-error" role="alert" className="text-xs text-destructive">{errors.title.message}</p>}
          </div>

          <div className="space-y-1">
            <label htmlFor="kb-source" className="text-sm font-medium text-foreground">
              Source <span aria-hidden="true" className="text-destructive">*</span>
            </label>
            <input id="kb-source" type="text" placeholder="e.g., European Union, PT Civil Code"
              aria-required="true" aria-invalid={!!errors.source}
              aria-describedby={errors.source ? 'kb-source-error' : undefined}
              className={fieldClass(!!errors.source)} {...register('source')} />
            {errors.source && <p id="kb-source-error" role="alert" className="text-xs text-destructive">{errors.source.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label htmlFor="kb-doc-type" className="text-sm font-medium text-foreground">
                Document Type <span aria-hidden="true" className="text-destructive">*</span>
              </label>
              <select id="kb-doc-type" aria-required="true" aria-invalid={!!errors.doc_type}
                className={fieldClass(!!errors.doc_type)} {...register('doc_type')}>
                <option value="">Select type…</option>
                {DOC_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {errors.doc_type && <p role="alert" className="text-xs text-destructive">{errors.doc_type.message}</p>}
            </div>
            <div className="space-y-1">
              <label htmlFor="kb-jurisdiction" className="text-sm font-medium text-foreground">Jurisdiction</label>
              <input id="kb-jurisdiction" type="text" placeholder="e.g., EU, PT, DE"
                className={fieldClass(false)} {...register('jurisdiction')} />
            </div>
          </div>

          <div className="space-y-1">
            <label htmlFor="kb-effective-date" className="text-sm font-medium text-foreground">Effective Date</label>
            <input id="kb-effective-date" type="date" className={fieldClass(false)} {...register('effective_date')} />
          </div>

          <p className="flex items-start gap-2 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" />
            After upload, the document will be automatically chunked and indexed for AI retrieval. This may take a few minutes.
          </p>
        </form>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction form="kb-upload-form" type="submit"
            className={cn(buttonVariants())} disabled={isPending} aria-busy={isPending}>
            {isPending ? 'Uploading…' : 'Upload & Index'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
